/**
 * Extracts a user-facing message from a Clerk API error, falling back to a caller-supplied
 * default when Clerk didn't provide one (or `err` isn't a Clerk error shape at all).
 */
export function getClerkErrorMessage(err: unknown, fallback: string): string {
  const clerkErr = err as { errors?: { message: string }[] } | undefined;
  return clerkErr?.errors?.[0]?.message ?? fallback;
}

type StatusHandlers<TResult extends { status: string | null }> = Partial<{
  [S in NonNullable<TResult['status']>]: (result: TResult) => void | Promise<void>;
}> & {
  fallback: (result: TResult) => void | Promise<void>;
};

/**
 * Dispatches on a Clerk resource's `status` field. `fallback` is required, so there is no way
 * to write a call site that silently does nothing when Clerk resolves with a status nobody
 * explicitly handled — the compiler enforces every call site to say what happens for "anything
 * else." Deliberately non-throwing: an unhandled-but-valid status (e.g. a form validation
 * failure Clerk reports without rejecting the promise) isn't the same failure class as a thrown
 * exception, so it shouldn't be forced through the same `catch` block.
 */
export async function matchClerkStatus<TResult extends { status: string | null }>(
  result: TResult,
  handlers: StatusHandlers<TResult>,
): Promise<void> {
  const handler = result.status
    ? (handlers as Record<string, (result: TResult) => void | Promise<void>>)[result.status]
    : undefined;
  await (handler ?? handlers.fallback)(result);
}

/**
 * Recovers from a Clerk error that may actually indicate success server-side (e.g. a duplicate
 * submit where the first request already completed verification and the second rejects with
 * "already verified"). Always reloads the resource from the server rather than trusting a
 * closed-over local snapshot, which may be stale from before the error occurred. Returns the
 * session id if the reloaded resource is genuinely complete, `null` otherwise (including if the
 * reload itself fails).
 */
export async function recoverCompletedSession(resource: {
  reload: () => Promise<{ status: string | null; createdSessionId?: string | null }>;
}): Promise<string | null> {
  const reloaded = await resource.reload().catch(() => null);
  if (reloaded?.status === 'complete' && reloaded.createdSessionId) {
    return reloaded.createdSessionId;
  }
  return null;
}
